/**
 * Sales line totals aligned with Signage create/edit order table:
 * - Unit Price column (view) = unit_retail_price || unit_price
 * - Discount = (unit_retail_price − changed_unit_price) × qty
 * - Total = changed_unit_price × qty + tax
 */

import { getSignageOrderLinePricing } from "components/DiscountLabelBadge";

function n(v: unknown): number {
  const x = Number(v);
  return Number.isFinite(x) ? x : 0;
}

function r2(v: number): number {
  return Math.round(v * 100) / 100;
}

/** Catalog / list unit price (unit_retail_price). */
export function getSalesOrderLineUnitRetailPrice(it: any): number {
  return r2(n(it?.unit_retail_price ?? it?.unit_price ?? it?.unit_purchase_price));
}

/** Effective changed unit price after assignment discount or manual edit. */
export function getSalesOrderLineChangedUnitPrice(it: any): number {
  const changed = n(it?.changed_unit_price ?? it?.changed_unitPrice);
  const retail = getSalesOrderLineUnitRetailPrice(it);
  return r2(changed > 0 ? changed : retail);
}

/** Per-line coupon / order discount amount on the row. */
export function getSalesOrderLineCouponDiscount(it: any): number {
  return r2(n(it?.discount_on_total_quantity ?? it?.discount ?? it?.discount_amount ?? 0));
}

/** Unit Price column helper (catalog retail). */
export function getSalesOrderLineUnitPrice(it: any): number {
  return getSalesOrderLineUnitRetailPrice(it);
}

export function getSalesOrderLinePricing(it: any): {
  actualUnitPrice: number;
  changedUnitPrice: number;
  qty: number;
  rowDiscount: number;
  couponDiscount: number;
  tax: number;
  lineTotal: number;
} {
  const qty = Math.max(1, n(it?.quantity));
  const actualUnitPrice = getSalesOrderLineUnitRetailPrice(it);
  const changedUnitPrice = getSalesOrderLineChangedUnitPrice(it);
  const tax = n(it?.item_tax_info?.total_product_tax_amount);
  const couponDiscount = getSalesOrderLineCouponDiscount(it);
  const { rowDiscount, lineTotal } = getSignageOrderLinePricing({
    actualUnitPrice,
    changedUnitPrice,
    qty,
    tax,
  });
  return {
    actualUnitPrice,
    changedUnitPrice,
    qty,
    rowDiscount,
    couponDiscount,
    tax,
    lineTotal,
  };
}

/** Discount column: (actual − changed) × qty. */
export function getSalesOrderLineDiscount(it: any): number {
  return getSalesOrderLinePricing(it).rowDiscount;
}

/** Line total column: changed × qty + tax. */
export function getSalesOrderLineTotalAmount(it: any): number {
  return getSalesOrderLinePricing(it).lineTotal;
}

/** Sub total before order-level discount (sum of changed × qty per line). */
export function computeSalesSubtotalBeforeDiscountFromItems(items: any[]): number {
  if (!Array.isArray(items)) return 0;
  return r2(
    items.reduce((sum, it) => sum + n(it?.quantity) * getSalesOrderLineChangedUnitPrice(it), 0)
  );
}

export function computeSalesSubtotalFromItems(items: any[]): number {
  return computeSalesSubtotalBeforeDiscountFromItems(items);
}

export function computeSalesTaxAmount(items: any[], effOrder: any, specialFeesTax: number): number {
  const productsTax = Array.isArray(items)
    ? items.reduce((sum: number, it: any) => sum + Number(it?.item_tax_info?.total_product_tax_amount || 0), 0)
    : 0;
  const shippingTax =
    effOrder?.order_charges?.[0]?.shipping_option?.reduce((sum: number, ship: any) => {
      return sum + Number(ship?.shipping_tax_amount || 0);
    }, 0) || 0;
  return productsTax + shippingTax + specialFeesTax;
}
